import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import './App.css';
import AdminLayout from './layouts/AdminLayout';
import TableDemo from './pages/Demo';
import { ROUTES } from './routes/path';
import { authService } from './services';
import { RoleEnum } from './types';

// Pages - to be created
const Login = React.lazy(() => import('./pages/auth/Login'));
const Dashboard = React.lazy(() => import('./pages/dashboard/Dashboard'));
const UserManagementItems = React.lazy(() => import('./pages/setting/UserManagementItems'));
const FunctionItems = React.lazy(() => import('./pages/setting/function/FunctionItems'));
const FeatureItems = React.lazy(() => import('./pages/setting/function/FeatureItems'));
const CacheSystem = React.lazy(() => import('./pages/setting/CacheSystem'));
const UserProfile = React.lazy(() => import('./pages/user/UserProfile'));
const RewardList = React.lazy(() => import('./pages/reward/RewardList'));
const RewardEdit = React.lazy(() => import('./pages/reward/RewardEdit'));
const RewardCreate = React.lazy(() => import('./pages/reward/RewardCreate'));
const PackageList = React.lazy(() => import('./pages/package/PackageList'));
const PackageEdit = React.lazy(() => import('./pages/package/PackageEdit'));
const PackageCreate = React.lazy(() => import('./pages/package/PackageCreate'));
const BackgroundList = React.lazy(() => import('./pages/background/BackgroundList'));
const BackgroundEdit = React.lazy(() => import('./pages/background/BackgroundEdit'));
const BackgroundCreate = React.lazy(() => import('./pages/background/BackgroundCreate'));
const ShareLinkList = React.lazy(() => import('./pages/share-link/ShareLinkList'));
const ShareLinkEdit = React.lazy(() => import('./pages/share-link/ShareLinkEdit'));
const ShareLinkCreate = React.lazy(() => import('./pages/share-link/ShareLinkCreate'));
const LogReward = React.lazy(() => import('./pages/logs/LogReward'));
const LogPlayerTurns = React.lazy(() => import('./pages/logs/LogPlayerTurns'));
const LogPlayerHexagram = React.lazy(() => import('./pages/logs/LogPlayerHexagram'));
const LogPlayerLuckyMoney = React.lazy(() => import('./pages/logs/LogPlayerLuckyMoney'));

const RulesList = React.lazy(() => import('./pages/rules/RulesList'));
const RulesEdit = React.lazy(() => import('./pages/rules/RulesEdit'));
const RulesCreate = React.lazy(() => import('./pages/rules/RulesCreate'));

const HexagramItems = React.lazy(() => import('./pages/hexagram/HexagramItems'));
const HexagramCreate = React.lazy(() => import('./pages/hexagram/HexagramCreate'));
const HexagramDetail = React.lazy(() => import('./pages/hexagram/HexagramDetail'));
const HexagramEdit = React.lazy(() => import('./pages/hexagram/HexagramEdit'));

const SuggestionsItems = React.lazy(() => import('./pages/suggestions/SuggestionsItems'));
const SuggestionsCreate = React.lazy(() => import('./pages/suggestions/SuggestionsCreate'));
const SuggestionsDetail = React.lazy(() => import('./pages/suggestions/SuggestionsDetail'));
const SuggestionsEdit = React.lazy(() => import('./pages/suggestions/SuggestionsEdit'));

// Protected route component
const ProtectedRoute = ({ children, allowed = RoleEnum.Dashboard }: { children: React.ReactNode; allowed?: RoleEnum }) => {
  const isAllowed = authService.isAllowedPermission(allowed);

  if (!authService.isAuthenticated()) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (authService.isAuthenticated() && !isAllowed) {
    return <Navigate to={ROUTES.FORBIDDEN} replace />;
  }

  return <>{children}</>;
};

// Page not found route
const PageNotFound = () => {
  return (
    <AdminLayout title="Not Found">
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" component="h1" gutterBottom>
            Không tìm thấy trang
          </Typography>
        </Stack>
      </Box>
    </AdminLayout>
  );
};

const PageForbidden = () => {
  return (
    <AdminLayout title="Forbidden">
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" component="h1" gutterBottom>
            Bạn không có quyền truy cập trang này
          </Typography>
        </Stack>
      </Box>
    </AdminLayout>
  );
};

function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <React.Suspense fallback={<LinearProgress color="secondary" />}>
          <Routes>
            <Route path={ROUTES.LOGIN} element={<Login />} />
            <Route
              path={ROUTES.DASHBOARD}
              element={
                <ProtectedRoute allowed={RoleEnum.Dashboard}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.SETTING.USER_MANAGEMENT}
              element={
                <ProtectedRoute allowed={RoleEnum.Admin}>
                  <UserManagementItems />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.HEXAGRAM.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <HexagramItems />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.HEXAGRAM.CREATE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <HexagramCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.HEXAGRAM.EDIT()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <HexagramEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.HEXAGRAM.DETAIL()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <HexagramDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.SUGGESTIONS.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <SuggestionsItems />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.SUGGESTIONS.CREATE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <SuggestionsCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.SUGGESTIONS.EDIT()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <SuggestionsEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.SUGGESTIONS.DETAIL()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <SuggestionsDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.USER_PROFILE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <UserProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.SETTING.FUNCTION}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <FunctionItems />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.DEMO}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <TableDemo />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.SETTING.FEATURE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <FeatureItems />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.REWARD.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <RewardList />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.REWARD.CREATE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <RewardCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.REWARD.EDIT()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <RewardEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.PACKAGE.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <PackageList />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.PACKAGE.CREATE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <PackageCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.PACKAGE.EDIT()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <PackageEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.BACKGROUND.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <BackgroundList />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.BACKGROUND.CREATE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <BackgroundCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.BACKGROUND.EDIT()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <BackgroundEdit />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.CONFIG.SHARE_LINK.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <ShareLinkList />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.SHARE_LINK.CREATE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <ShareLinkCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.SHARE_LINK.EDIT()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <ShareLinkEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.RULES.EDIT()}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <RulesEdit />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.CONFIG.RULES.LIST}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <RulesList />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CONFIG.RULES.CREATE}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <RulesCreate />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.SETTING.CACHE_SYSTEM}
              element={
                <ProtectedRoute allowed={RoleEnum.Admin}>
                  <CacheSystem />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.LOG.REWARD}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <LogReward />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.LOG.HEXAGRAM}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <LogPlayerHexagram />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.LOG.LUCKY_MONEY}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <LogPlayerLuckyMoney />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.LOG.PLAYER_TURNS}
              element={
                <ProtectedRoute allowed={RoleEnum.Editor}>
                  <LogPlayerTurns />
                </ProtectedRoute>
              }
            />

            <Route path={ROUTES.NOT_FOUND} element={<PageNotFound />} />
            <Route path={ROUTES.FORBIDDEN} element={<PageForbidden />} />
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </React.Suspense>
      </BrowserRouter>
    </LocalizationProvider>
  );
}

export default App;
